import { useCallback } from 'react';
import { useNoticeBannersStackContext } from '@sg/garnish';

import { useBraze } from '@order/Braze';
import { SWEETPASS_BRAZE_CUSTOM_EVENTS } from '@order/constants';
import type { TierName } from '@order/graphql';
import { useCancelSubscriptionAtCycleEndMutation } from '@order/graphql';
import { useFeatureFlag } from '@order/LaunchDarkly';

import type { AccountSweetpassCancellationSection } from '../useSweetpassAccountContentfulData';
import {
  ACCOUNT_SWEETPASS_CANCELLATION_SECTION,
  useSweetpassAccountContentfulData,
} from '../useSweetpassAccountContentfulData';

export const useCancelSweetpassMembership = (
  props: CancelSweetpassMembershipProps,
) => {
  const { subscriptionId, nextBillingDate, tierName, refetchMembership } =
    props;

  const { content } = useSweetpassAccountContentfulData();
  const cancellationSection = content?.find?.(
    (section) => section.name === ACCOUNT_SWEETPASS_CANCELLATION_SECTION,
  ) as AccountSweetpassCancellationSection;
  const {
    cancellationDataGenericErrorMessage,
    cancellationDataGenericSuccessMessage,
  } = cancellationSection ?? {};

  const { push: addNoticeBanner } = useNoticeBannersStackContext();
  const { logCustomEvent } = useBraze();
  const isSweetpassCustomEventsLogEnabled = useFeatureFlag(
    'CELS-618-short-custom-sweetpass-braze-events-enabled',
  );

  const [, cancelSubscriptionAtCycleEndMutation] =
    useCancelSubscriptionAtCycleEndMutation();

  const logCancellationCustomBrazeEvent = useCallback(() => {
    if (!isSweetpassCustomEventsLogEnabled) return;

    if (tierName === 'SWEETPASS') {
      logCustomEvent(
        SWEETPASS_BRAZE_CUSTOM_EVENTS.UserCancelledSweetpassSubscription,
      );
    }

    if (tierName === 'SWEETPASS_PLUS') {
      logCustomEvent(
        SWEETPASS_BRAZE_CUSTOM_EVENTS.UserCancelledSweetpassPlusSubscription,
      );
    }
  }, [isSweetpassCustomEventsLogEnabled, logCustomEvent, tierName]);

  const cancelSubscriptionAtCycleEnd = useCallback(async () => {
    if (!subscriptionId) return;

    const { data: cancelSubscriptionData } =
      await cancelSubscriptionAtCycleEndMutation({
        subscriptionId,
      });

    const hasFailedToCancel =
      cancelSubscriptionData?.cancelSubscriptionAtCycleEnd?.__typename !==
      'CancelSubscriptionAtCycleEndSuccessResponse';

    if (hasFailedToCancel) {
      addNoticeBanner({
        text: cancellationDataGenericErrorMessage,
        palette: 'caution',
      });

      return;
    }

    addNoticeBanner({
      text: cancellationDataGenericSuccessMessage.replace(
        '{nextBillingDate}',
        nextBillingDate ?? '',
      ),
      palette: 'success',
    });

    logCancellationCustomBrazeEvent();

    refetchMembership();
  }, [
    subscriptionId,
    cancellationDataGenericSuccessMessage,
    cancellationDataGenericErrorMessage,
    nextBillingDate,
    logCancellationCustomBrazeEvent,
    cancelSubscriptionAtCycleEndMutation,
    addNoticeBanner,
    refetchMembership,
  ]);

  return { cancelSubscriptionAtCycleEnd };
};

type CancelSweetpassMembershipProps = {
  readonly tierName: TierName | undefined;
  readonly subscriptionId?: string;
  readonly nextBillingDate?: string;
  readonly refetchMembership: () => void;
};
